import { Transition } from "@mantine/core";
import { useReducedMotion } from "@mantine/hooks";
import { useCallback, useEffect, useState } from "react";
import VoiceOrb from "../voice/VoiceOrb";
import useVoiceConversation from "../voice/useVoiceConversation";
import DockBarFace from "./DockBarFace";
import DockCardScrim from "./DockCardScrim";
import DockPillFace from "./DockPillFace";
import DockReviewCard from "./DockReviewCard";
import useReviewCardState from "./useReviewCardState";
import css from "./AssistantDock.module.scss";

const REVIEW_TRANSITION_MS = 380;
const REVIEW_CARD_TRANSITION = {
  in: { opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" },
  out: { opacity: 0, transform: "translate3d(0, 1.25rem, 0) scale(0.985)" },
  transitionProperty: "opacity, transform",
};

// One constant render resolution for both shapes. The carrier's CSS scales the
// canvas to 100%, so the visible size comes from --dock-orb-size; feeding
// orb-ui a changing `size` would resize its canvas mid-morph for no gain.
const ORB_RENDER_SIZE = 40;

// One persistent bottom-centre element that morphs between a pill and a bar.
//
// It replaces the old full-screen VoiceModeOverlay: there is no backdrop, no
// blur layer and no portal, so the app stays visible and clickable throughout a
// session. The column is pointer-events: none and only the bar and the review
// card take pointer events back.
//
// It also never unmounts. That is deliberate on two counts: the orb's WebGL
// context survives the morph instead of being torn down and rebuilt on every
// open, and useVoiceConversation lives here rather than in FloatingMicAssistant
// so a streamed token re-renders this subtree only — not the chat drawer.
// Mounting is free: neither the STT nor the TTS hook touches the microphone,
// a socket or an AudioContext until connect() is called.
const AssistantDock = ({
  opened,
  hidden,
  onOpen,
  onClose,
  onOpenChat,
  sendMessage,
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  // How tall the current utterance wants the bar to be, in whole lines. Held
  // here rather than inside the bar face because the shell it sizes is a
  // sibling; it changes on a line boundary, not per streamed token.
  const [speechLines, setSpeechLines] = useState(1);
  const reduceMotion = useReducedMotion();

  const {
    voiceState,
    userTranscript,
    assistantText,
    statusLabel,
    errorMessage,
    reviewMessage,
    clearReviewMessage,
    isMicMuted,
    startSession,
    stopSession,
    toggleMicMuted,
    getMicStream,
    getTtsAnalyser,
  } = useVoiceConversation({ sendMessage, onAutoClose: onClose });

  const { isReviewOpen, openReview, collapseReview, handleReviewExited } =
    useReviewCardState({ opened, reviewMessage, clearReviewMessage });

  useEffect(() => {
    if (!opened) return undefined;
    startSession();
    return () => stopSession();
    // startSession/stopSession are stable; opened drives the session.
  }, [opened, startSession, stopSession]);

  // Escape is scoped to the dock's own subtree. A document-level listener would
  // swallow Escape from every modal, dropdown and drawer on the page behind us,
  // which was harmless under a full-screen overlay and is not harmless now.
  const handleKeyDown = useCallback(
    (event) => {
      if (event.key !== "Escape") return;
      onClose();
    },
    [onClose],
  );

  const handleOpen = useCallback(() => {
    setIsMinimized(false);
    onOpen();
  }, [onOpen]);

  const mode = opened ? "bar" : "pill";
  const isCompactActive = !hidden && mode === "pill" && isMinimized;
  const reviewDuration = reduceMotion ? 0 : REVIEW_TRANSITION_MS;

  return (
    <div
      className={`${css.column} ${hidden ? css.columnHidden : ""}`}
      data-mode={mode}
      data-minimized={mode === "pill" && isMinimized}
      style={{ "--dock-speech-lines": speechLines }}
    >
      {opened && reviewMessage && isReviewOpen ? <DockCardScrim /> : null}

      <Transition
        mounted={Boolean(opened && reviewMessage && isReviewOpen)}
        transition={REVIEW_CARD_TRANSITION}
        duration={reviewDuration}
        exitDuration={reviewDuration}
        timingFunction="cubic-bezier(0.16, 1, 0.3, 1)"
        onExited={handleReviewExited}
      >
        {(transitionStyles) => (
          <div className={css.cardSlot}>
            <DockReviewCard
              message={reviewMessage}
              style={transitionStyles}
              onCollapse={collapseReview}
            />
          </div>
        )}
      </Transition>

      <div
        className={css.shell}
        role="region"
        aria-label="Voice assistant"
        onKeyDown={handleKeyDown}
      >
        {/* Sits between the two faces, owned by neither, so the shader keeps
            running across the morph instead of remounting. */}
        <span className={css.orbCarrier} aria-hidden="true">
          {/* One orb for both shapes, never swapped for a different component:
              the idle pill has always shimmered by claiming "listening", and
              the shader animates on its own with no microphone behind it. */}
          <VoiceOrb
            size={ORB_RENDER_SIZE}
            voiceState={opened ? voiceState : "listening"}
            isMicMuted={isMicMuted}
            micStream={
              opened && voiceState === "listening" ? getMicStream() : null
            }
            ttsAnalyser={
              opened && voiceState === "speaking" ? getTtsAnalyser() : null
            }
          />
        </span>

        <DockPillFace
          hidden={hidden || mode === "bar"}
          isMinimized={isMinimized}
          onOpen={handleOpen}
          onMinimize={() => setIsMinimized(true)}
        />

        <DockBarFace
          hidden={hidden || mode === "pill"}
          voiceState={voiceState}
          userTranscript={userTranscript}
          assistantText={assistantText}
          statusLabel={statusLabel}
          errorMessage={errorMessage}
          isMicMuted={isMicMuted}
          hasPendingReview={Boolean(reviewMessage && !isReviewOpen)}
          onSpeechLinesChange={setSpeechLines}
          onOpenReview={openReview}
          onToggleMute={toggleMicMuted}
          onOpenChat={onOpenChat}
          onClose={onClose}
        />

        {/* Restores the whole pill from its minimized corner state. */}
        <button
          type="button"
          className={css.compactAction}
          aria-label="Start voice conversation"
          aria-hidden={!isCompactActive}
          disabled={!isCompactActive}
          tabIndex={isCompactActive ? 0 : -1}
          title="Assistant"
          onClick={handleOpen}
        />
      </div>
    </div>
  );
};

export default AssistantDock;
